import { z } from 'zod';
import { CurrencySchema, MoneyStringSchema } from './common.js';

export const CostBaseSchema = z.enum(['materia_prima', 'mao_obra', 'custo_direto', 'venda']);

/**
 * Centros de custo da SEGREGAÇÃO automática (destino dos custos no faturamento) — espelha
 * os tipos fixos do enum CaixaKind do Prisma (RF-FIN-03). NÃO inclui `personalizado`:
 * centros de custo personalizados existem só para lançamentos manuais e nunca recebem
 * composição automática de custo de produto (senão o resolver rotearia de forma ambígua).
 */
export const CaixaKindSchema = z.enum([
  'materia_prima',
  'salario',
  'manutencao',
  'caja_chica',
  'operacao',
  'variaveis',
  'impostos',
  'margem',
  'contrapartida',
]);
export type CaixaKind = z.infer<typeof CaixaKindSchema>;

export const CostItemTypeSchema = z.enum(['percentual', 'absoluto']);

const PercentualStringSchema = z.string().regex(/^\d+(\.\d{1,6})?$/, 'Percentual inválido');

export const CostItemDefSchema = z.discriminatedUnion('tipo', [
  z.object({
    tipo: z.literal('percentual'),
    nome: z.string().min(1),
    caixaKind: z.string().min(1),
    percentual: PercentualStringSchema,
    base: CostBaseSchema,
  }),
  z.object({
    tipo: z.literal('absoluto'),
    nome: z.string().min(1),
    caixaKind: z.string().min(1),
    valorAbsoluto: MoneyStringSchema,
  }),
]);
export type CostItemDefDTO = z.infer<typeof CostItemDefSchema>;

/**
 * Cadastro de um item de custo global do tenant (RF-PRO-04). Cada item gera uma
 * caixinha (`caixaKind`) ao ser aplicado num orçamento. `percentual` incide sobre
 * a `base`; `absoluto` soma um valor fixo por 100 L.
 */
export const CreateCostItemDefSchema = z.discriminatedUnion('tipo', [
  z.object({
    tipo: z.literal('percentual'),
    nome: z.string().min(1),
    caixaKind: CaixaKindSchema,
    percentual: PercentualStringSchema,
    base: CostBaseSchema,
    ordem: z.number().int().min(0).optional(),
    ativo: z.boolean().optional(),
  }),
  z.object({
    tipo: z.literal('absoluto'),
    nome: z.string().min(1),
    caixaKind: CaixaKindSchema,
    valorAbsoluto: MoneyStringSchema,
    ordem: z.number().int().min(0).optional(),
    ativo: z.boolean().optional(),
  }),
]);
export type CreateCostItemDefDTO = z.infer<typeof CreateCostItemDefSchema>;

/** Atualização parcial — o formulário envia o conjunto completo; a lista faz toggles pontuais (ativo/ordem). */
export const UpdateCostItemDefSchema = z
  .object({
    nome: z.string().min(1).optional(),
    tipo: CostItemTypeSchema.optional(),
    caixaKind: CaixaKindSchema.optional(),
    percentual: PercentualStringSchema.nullable().optional(),
    base: CostBaseSchema.nullable().optional(),
    valorAbsoluto: MoneyStringSchema.nullable().optional(),
    ordem: z.number().int().min(0).optional(),
    ativo: z.boolean().optional(),
  })
  .refine((dto) => Object.keys(dto).length > 0, { message: 'Nada para atualizar' });
export type UpdateCostItemDefDTO = z.infer<typeof UpdateCostItemDefSchema>;

export const CostItemDefViewSchema = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  tipo: CostItemTypeSchema,
  caixaKind: CaixaKindSchema,
  percentual: z.string().nullable(),
  base: CostBaseSchema.nullable(),
  valorAbsoluto: MoneyStringSchema.nullable(),
  ordem: z.number().int(),
  ativo: z.boolean(),
});
export type CostItemDefViewDTO = z.infer<typeof CostItemDefViewSchema>;

export const CreateProductSchema = z.object({
  nome: z.string().min(1),
  sku: z.string().optional(),
  materiaPrima: MoneyStringSchema,
  moeda: CurrencySchema.optional(),
});
export type CreateProductDTO = z.infer<typeof CreateProductSchema>;

export const UpdateProductSchema = z.object({
  nome: z.string().min(1).optional(),
  sku: z.string().optional(),
  materiaPrima: MoneyStringSchema.optional(),
  ativo: z.boolean().optional(),
});
export type UpdateProductDTO = z.infer<typeof UpdateProductSchema>;

export const CostLineViewSchema = z.object({
  nome: z.string(),
  caixaKind: z.string(),
  valor: MoneyStringSchema,
  percentual: z.number(),
});

export const ProductViewSchema = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  sku: z.string().nullable(),
  materiaPrima: MoneyStringSchema,
  moeda: CurrencySchema,
  ativo: z.boolean(),
  composicao: z.object({
    total: MoneyStringSchema,
    lines: z.array(CostLineViewSchema),
  }),
});
export type ProductViewDTO = z.infer<typeof ProductViewSchema>;
